/**
 * HourlySummaryPage — V2 main orchestrator for the Hourly Summary Report.
 *
 * Full report page with: time range selection, report-level filters,
 * summary + detailed views, CSV/PDF export, event notes, reason editing.
 *
 * Follows the AlertDetailsPage.jsx pattern precisely with hourly-specific
 * summary data (key metrics, top 5 residents, hourly breakdown table).
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
    getUsername,
    getCampusConfig,
    getCampusTzOffset,
} from '../../../services/session.js';
import { iAmHigherAuthority, formatDateTimeIso, formatDateTime12Hour, getToday, convertIsoToLocal, shiftDateStr, campusNow } from '../../../libs.jsx';
import { exportAlertDetailsCsv, exportAlertDetailsPdf, exportHourlySummaryCsv, exportHourlySummaryPdf } from '../../../services/csvExport.js';
import {
    fetchHourlySummaryReport,
    fetchFilterOptions,
    fetchPreformNotes,
    addEventNote,
    editEventReason,
} from './api/hourlySummaryApi.js';

import AlertDetailsTable, { COLUMN_DEFS } from '../alert-details/components/AlertDetailsTable.jsx';
import HourlySummaryView from './components/HourlySummaryView.jsx';
import ReportFilterDrawer from '../alert-details/components/ReportFilterDrawer.jsx';
import TimeWindowPicker from '../../../components/v2/TimeWindowPicker.jsx';
import Pagination from '../../../components/v2/Pagination.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import { DEFAULT_TIME_WINDOW, timeWindowToValues, describeTimeWindow, normalizeTimeWindow }
    from '../../../utils/reportTimeWindow.js';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';
import ColumnVisibilityToggle from '../../../components/v2/ColumnVisibilityToggle.jsx';
import EventNoteDialog from '../../../components/v2/EventNoteDialog.jsx';
import EditReasonDialog from '../alert-details/components/EditReasonDialog.jsx';
import usePersistedColumns from '../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';
import { fetchCallLogsReport } from '../call-logs/api/callLogsReportApi.js';
import useReportRestore from '../../../hooks/useReportRestore.js';
import { ReportRestoreOffer, ReportRestoreNote }
    from '../../../components/v2/ReportRestoreBanner.jsx';

import './hourly-summary-page.css';
import { GRANDSTREAM_ONLY_COLUMNS, timeToConnectFilterValue } from '../alert-details/timeToConnect.js';
import { sortRows } from '../../../utils/sortRows.js';
import { expandReasonIds } from '../../../utils/reasonFilterIds.js';
import { REPORT_TYPE_ID } from '../../../utils/reportTypes.js';
import { buildFilterChips, countActiveFilters } from '../../../utils/reportFilterChips.js';
import { TIME_RANGES, initialRangeState, rangeStateFor, reportErrorMessage, clearCampusFilters } from '../../../utils/reportPageState.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/* Cache scope. A literal, not the route param: rename a route and a
   param-derived key silently misses every cached report. */
const REPORT_TYPE = 'hourly';


function emptyReportFilters() {
    return {
        areas: [],
        areaGroups: [],
        areaTypes: [],
        residents: [],
        residentGroups: [],
        users: [],
        userGroups: [],
        deviceCategories: [],
        devices: [],
        eventTypes: [],
        reasons: [],
        responseTime: { enabled: false, operator: 'lt', value: 60 },
        /* Time-of-day window, minutes from midnight, half-open. [0, 1440] is all day and
           the query treats it as a no-op — see utils/reportTimeWindow.js. */
        timeWindow: { ...DEFAULT_TIME_WINDOW },
    };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const HourlySummaryPage = ({ run }) => {
    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const tzOffset = getCampusTzOffset(campusId);
    const showCampusSelector = useMemo(
        () => iAmHigherAuthority(30, session?.role_weight),
        [session]
    );
    const eventNotesActive = Boolean(campusConfig?.event_notes_active);
    const eventNotesPreform = Boolean(campusConfig?.event_notes_preform);
    const eventNotesFreeform = Boolean(campusConfig?.event_notes_freeform);
    const isReasonEditable = Boolean(campusConfig?.is_reason_editable);
    // Grandstream campuses get a Calls column (call logs merged into events by event_id).
    const isGrandstream = Boolean(campusConfig?.is_grandstream);

    // -- Report restoration (history stamp + result cache + nav-menu offer) --
    /* Supplies the navigation state this page used to read directly
       (ScheduledReportsPage "Run Report Local", and a stamped history entry on
       browser back), and owns the router bindings that went with it. */
    const restore = useReportRestore({ reportType: REPORT_TYPE, campusId, autoRun: run });
    const { lookup: cacheLookup, commit: cacheCommit, endRefresh } = restore;
    const navState = restore.navState;

    // -- Report state --
    const [status, setStatus] = useState('init'); // 'init' | 'loading' | 'ready'
    const [reportData, setReportData] = useState([]);
    const [summaryData, setSummaryData] = useState([]);
    const [hourlyStats, setHourlyStats] = useState({});
    const [overLimit, setOverLimit] = useState(false);
    const [csvUrl, setCsvUrl] = useState('');

    // -- Time range --
    const initRange = initialRangeState(navState);
    const [rangeId, setRangeId] = useState(initRange.rangeId);
    const [rangeName, setRangeName] = useState(initRange.rangeName);
    const [customRange, setCustomRange] = useState(initRange.customRange);
    const [start, setStart] = useState(() => {
        if (navState?.startDate) return new Date(navState.startDate);
        const d = getToday(-1); d.setHours(0,0,0,0); return d;
    });
    const [end, setEnd] = useState(() => {
        if (navState?.endDate) return new Date(navState.endDate);
        const d = getToday(-1); d.setHours(23,59,59,999); return d;
    });
    const [startDisplay, setStartDisplay] = useState('');
    const [endDisplay, setEndDisplay] = useState('');

    // -- Report filters (pre-query) — pre-fill from navigation state if present --
    const [reportFilters, setReportFilters] = useState(() => {
        if (navState?.filters) return { ...emptyReportFilters(), ...navState.filters };
        return emptyReportFilters();
    });
    const [filterOptions, setFilterOptions] = useState({});
    // True once the filter-options fetch has finished, whether or not it succeeded.
    const [optionsSettled, setOptionsSettled] = useState(false);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // -- Schedule drawer --
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);

    // -- View mode --
    const [viewMode, setViewMode] = useState('summary'); // 'summary' | 'detailed'

    // -- Table state (detailed view) --
    const [search, setSearch] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [sortColumn, setSortColumn] = useState('event_datetime_local');
    const [sortOrder, setSortOrder] = useState('asc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();
    // Calls and Time to Connect only exist for Grandstream campuses, so build the
    // column set per-campus, exactly as AlertDetailsPage does. Non-Grandstream
    // campuses never see them in the table or the Columns toggle.
    const availableColumnDefs = useMemo(
        () => (isGrandstream ? COLUMN_DEFS : COLUMN_DEFS.filter((c) => !GRANDSTREAM_ONLY_COLUMNS.includes(c.key))),
        [isGrandstream]
    );
    const availableColumnKeys = useMemo(() => availableColumnDefs.map((c) => c.key), [availableColumnDefs]);
    // forceInclude surfaces newly-added columns ONCE for existing users who already have a
    // saved column preference, then records a "shown" marker so they can still toggle it
    // off if they want. New users get it via DEFAULT_VISIBLE / allColumnKeys as usual.
    const forceIncludeColumns = useMemo(
        () => (isGrandstream
            ? ['notes', 'calls', 'time_to_connect', 'estimated_location_name']
            : ['notes', 'estimated_location_name']),
        [isGrandstream]
    );
    const [visibleColumns, handleToggleColumn] = usePersistedColumns('report-hourly-summary', availableColumnKeys, availableColumnKeys, forceIncludeColumns);

    // -- Dialogs --
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteTarget, setNoteTarget] = useState(null);
    const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
    const [reasonTarget, setReasonTarget] = useState(null);
    const [prenoteData, setPrenoteData] = useState([]);

    // -- UI --
    const [saving, setSaving] = useState(false);
    const shouldRerunRef = useRef(false);
    const [timePopoverOpen, setTimePopoverOpen] = useState(false);
    const timePopoverRef = useRef(null);
    const pendingAutoRunRef = useRef(false);

    const { toast, showToast: showTypedToast, dismissToast } = useToast();
    /* This page's calls are (message, type); the shared hook is (type, message). */
    const showToast = useCallback((message, type = 'success') => showTypedToast(type, message), [showTypedToast]);

    // ======================================================================
    // Load filter options + preform notes on mount / campus change
    // ======================================================================
    useEffect(() => {
        if (!campusId) return;
        setOptionsSettled(false);
        fetchFilterOptions(campusId)
            .then((opts) => { setFilterOptions(opts); setOptionsSettled(true); })
            .catch(() => setOptionsSettled(true));
        fetchFrequencies().then((d) => setFrequencies(Array.isArray(d) ? d : []));
        if (eventNotesActive) {
            fetchPreformNotes(campusId).then(setPrenoteData);
        }
    }, [campusId, eventNotesActive]);

    // ======================================================================
    // Auto-run on mount (from `run` prop OR ScheduledReportsPage
    // "Run Report Local" navigation state).
    // ======================================================================
    // Armed at mount (consumeAutoRun is a one-shot); fired below once it is safe.
    const autoRunArmedRef = useRef(false);
    useEffect(() => {
        autoRunArmedRef.current = restore.consumeAutoRun();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* Accepting the nav-menu offer. Deferred through the page's existing
       pendingAutoRunRef rather than run here: setState is async, so a run
       started now would key the cache off the OLD config and miss. */
    const handleAcceptRestore = useCallback(() => {
        const cfg = restore.acceptOffer();
        if (!cfg) return;
        const r = rangeStateFor(cfg.rangeId ?? 0);
        setRangeId(r.rangeId);
        setRangeName(r.rangeName);
        setCustomRange(r.customRange);
        if (cfg.startDate) setStart(new Date(cfg.startDate));
        if (cfg.endDate) setEnd(new Date(cfg.endDate));
        if (cfg.filters) setReportFilters({ ...emptyReportFilters(), ...cfg.filters });
        pendingAutoRunRef.current = true;
    }, [restore]);

    // ======================================================================
    // Close time popover on outside click / Escape
    // ======================================================================
    /* Runs once on close for any dismissal route, so a window set in the popover takes
       effect without hunting for Run, and without firing mid-edit. Declared before the
       pendingAutoRunRef consumer below — effects fire in declaration order within a
       commit, which is what lets the flag be picked up in the same pass. */
    useEffect(() => {
        if (timePopoverOpen) return;
        if (!windowChangedInPopoverRef.current) return;
        windowChangedInPopoverRef.current = false;
        if (status !== 'init') pendingAutoRunRef.current = true;
    }, [timePopoverOpen, status]);

    useEffect(() => {
        if (!timePopoverOpen) return;
        const handleClick = (e) => {
            if (timePopoverRef.current && !timePopoverRef.current.contains(e.target)) {
                setTimePopoverOpen(false);
            }
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') setTimePopoverOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [timePopoverOpen]);

    // ======================================================================
    // Time range helpers
    // ======================================================================
    const handleTimeRange = useCallback((range) => {
        const [s, e] = range.getRange();
        setStart(s);
        setEnd(e);
        setRangeId(range.id);
        setRangeName(range.label);
        setCustomRange(false);
    }, []);

    const handleCustomToggle = useCallback(() => {
        setCustomRange(true);
        setRangeId(0);
        setRangeName('Custom');
    }, []);

    const handlePopoverCustom = useCallback(() => {
        setTimePopoverOpen(false);
        handleCustomToggle();
        setFilterDrawerOpen(true);
    }, [handleCustomToggle]);

    // ======================================================================
    // Run Report
    // ======================================================================
    /* `force` skips the cache. Every Run control passes it: a cache that
       ignored an explicit Run would be indistinguishable from a broken button. */
    const handleRunReport = useCallback(async ({ force = false } = {}) => {
        // Cap end to now if in future. Campus time, like `end` itself (campusNow, libs.jsx):
        // a browser clock behind the campus would clip the campus's latest alerts.
        const now = campusNow();
        const endToUse = end > now ? now : end;

        /* Key names match what the state initialisers read off navState -- the
           hook's one contract. */
        const runConfig = {
            rangeId,
            filters: reportFilters,
            startDate: start.toISOString(),
            endDate: endToUse.toISOString(),
        };
        const { hit, revalidating } = cacheLookup(runConfig, { force });

        /* The stats block rides in `extra`: restoring the table without it
           would put the hourly figures back blank. */
        if (hit) {
            setReportData(hit.records);
            setSummaryData(hit.summary || []);
            setHourlyStats(hit.extra?.hourlyStats || {});
            setOverLimit(hit.overLimit);
            setCsvUrl(hit.csvUrl);
            setPage(1);
            setStatus('ready');
            if (!revalidating) return;
        } else {
            setStatus('loading');
            setOverLimit(false);
        }

        const stD = formatDateTimeIso(start);
        const enD = formatDateTimeIso(endToUse);

        setStartDisplay(formatDateTime12Hour(start));
        setEndDisplay(formatDateTime12Hour(endToUse));

        // Build filter strings from arrays. Drawer keys reasons by
        // cloud_reason_code_id; combine with each row's matching reason_code_id.
        const combinedReasonIds = expandReasonIds(reportFilters.reasons, filterOptions.reasonCodes);
        const filterPayload = {
            areas: (reportFilters.areas || []).join(','),
            areaGroups: (reportFilters.areaGroups || []).join(','),
            areaTypes: (reportFilters.areaTypes || []).join(','),
            residents: (reportFilters.residents || []).join(','),
            residentGroups: (reportFilters.residentGroups || []).join(','),
            users: (reportFilters.users || []).join(','),
            userGroups: (reportFilters.userGroups || []).join(','),
            deviceCategories: (reportFilters.deviceCategories || []).join(','),
            devices: (reportFilters.devices || []).join(','),
            eventTypes: (reportFilters.eventTypes || []).join(','),
            reasons: combinedReasonIds.join(','),
            responseTime: reportFilters.responseTime?.enabled
                ? [true, reportFilters.responseTime.operator, reportFilters.responseTime.value]
                : [false, 'lt', 60],
            timeWindow: timeWindowToValues(reportFilters.timeWindow),
        };

        const result = await fetchHourlySummaryReport({
            campusId,
            username: getUsername(),
            startDate: stD,
            endDate: enD,
            filters: filterPayload,
            isCloud: Boolean(campusConfig?.is_cloud),
            isGrandstream,
        });

        if (result.ErrorCode) {
            showToast(reportErrorMessage(result.ErrorCode), 'error');
            /* A failed BACKGROUND refresh leaves the stale report up rather
               than blanking a report someone is reading. */
            if (revalidating) {
                endRefresh();
                return;
            }
            setStatus('init');
            return;
        }

        if (result.overlimit) {
            setOverLimit(true);
            setCsvUrl(result.url || '');
            showToast('Report exceeds 5,000 events. Details will need to be downloaded.', 'warning');
        } else {
            setOverLimit(false);
            setCsvUrl(result.url || '');
        }

        let records = result.records || [];

        // Grandstream campuses: fetch call logs for the same window and merge them
        // into events by event_id (mirrors how notes ride on each row) so the table
        // can show a per-event call count + hover. Non-fatal on failure — the report
        // still renders, just without call badges. Call logs proc wants DATE strings,
        // compared to UTC call_start_time, so pad a day each side: same fix and
        // reason as AlertDetailsPage.
        if (isGrandstream && records.length) {
            try {
                const callResp = await fetchCallLogsReport({
                    campusId,
                    startDate: shiftDateStr(stD, -1),
                    endDate: shiftDateStr(enD, 1),
                });
                const callRecords = callResp?.records || [];
                if (callRecords.length) {
                    const callsByEvent = new Map();
                    callRecords.forEach((c) => {
                        const key = String(c.event_id ?? '');
                        if (!key) return;
                        if (!callsByEvent.has(key)) callsByEvent.set(key, []);
                        callsByEvent.get(key).push(c);
                    });
                    callsByEvent.forEach((arr) => arr.sort(
                        (a, b) => String(a.call_start_time || '').localeCompare(String(b.call_start_time || ''))
                    ));
                    records = records.map((r) => ({
                        ...r,
                        calls: callsByEvent.get(String(r.event_id ?? '')) || [],
                    }));
                }
            } catch (err) {
                console.error('[HourlySummaryPage] Call logs merge failed:', err);
            }
        }

        const stats = {
            max: result.max,
            min: result.min,
            avg: result.avg,
            savg: result.savg,
            coverage: result.coverage,
            mostActive: result.mostActive || [],
            hasTimeToConnect: result.hasTimeToConnect,
            avgTimeToConnect: result.avgTimeToConnect,
            intercomUsagePct: result.intercomUsagePct,
        };
        setReportData(records);
        setSummaryData(result.summary || []);
        setHourlyStats(stats);
        setPage(1);
        setStatus('ready');

        cacheCommit(runConfig, {
            records,
            summary: result.summary || [],
            overLimit: Boolean(result.overlimit),
            csvUrl: result.url || '',
            extra: { hourlyStats: stats },
        });
    }, [filterOptions, start, end, campusId, reportFilters, showToast, isGrandstream, rangeId,
        cacheLookup, cacheCommit, endRefresh]);

    /* Fire the armed auto-run. Held until the filter options have settled when the
       run carries a reason filter: the request expands reasons to cloud + local ids
       from those options (utils/reasonFilterIds.js), and firing first would silently
       send cloud ids only and return fewer rows. Without a reason filter it runs
       at once, as before. Declared below handleRunReport so its dep array can list it. */
    useEffect(() => {
        if (!autoRunArmedRef.current) return;
        if ((reportFilters.reasons || []).length > 0 && !optionsSettled) return;
        autoRunArmedRef.current = false;
        handleRunReport();
    }, [optionsSettled, reportFilters, handleRunReport]);

    // ======================================================================
    // Auto-run after quick time range selection
    // ======================================================================
    useEffect(() => {
        if (pendingAutoRunRef.current) {
            pendingAutoRunRef.current = false;
            handleRunReport();
        }
    });

    const handleQuickTimeRange = useCallback((range) => {
        handleTimeRange(range);
        pendingAutoRunRef.current = true;
        setTimePopoverOpen(false);
    }, [handleTimeRange]);

    // ======================================================================
    // Re-run report after filter apply / clear (if already run)
    // ======================================================================
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [reportFilters, handleRunReport]);

    // ======================================================================
    // Data pipeline: search → column filters → sort → paginate
    // ======================================================================
    const searchedData = useMemo(() => {
        if (!search) return reportData;
        const term = search.toLowerCase();
        return reportData.filter((row) =>
            (row.resident_name || '').toLowerCase().includes(term) ||
            (row.area_name || '').toLowerCase().includes(term) ||
                (row.estimated_location_name || '').toLowerCase().includes(term) ||
            (row.description || '').toLowerCase().includes(term) ||
            (row.manufacture_uuid || '').toLowerCase().includes(term) ||
            (row.display_name || '').toLowerCase().includes(term)
        );
    }, [reportData, search]);

    const columnFilteredData = useMemo(() => {
        const activeFilters = Object.entries(columnFilters).filter(([, vals]) => vals.length > 0);
        if (activeFilters.length === 0) return searchedData;
        return searchedData.filter((row) =>
            activeFilters.every(([key, vals]) => {
                if (key === 'event_datetime_local') {
                    // Match by hour label (e.g. "3 PM") — field is already local, no offset needed
                    const local = convertIsoToLocal(row.event_datetime_local);
                    if (!local) return false;
                    const match = local.match(/(\d{1,2}):\d{2}(AM|PM)/i);
                    const hourLabel = match ? `${match[1]} ${match[2].toUpperCase()}` : '';
                    return vals.includes(hourLabel);
                }
                // Time to Connect matches the text its cell shows (minutes / No answer / N/A).
                if (key === 'time_to_connect') return vals.includes(timeToConnectFilterValue(row));
                const val = row[key] != null ? String(row[key]) : '';
                return vals.includes(val);
            })
        );
    }, [searchedData, columnFilters]);

    // Blank response/service times sort last, not as the fastest (utils/sortRows.js).
    const sortedData = useMemo(
        () => sortRows(columnFilteredData, sortColumn, sortOrder, { numericKeys: ['response_time', 'service_time', 'time_to_connect'] }),
        [columnFilteredData, sortColumn, sortOrder],
    );

    const totalFiltered = sortedData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const paginatedData = useMemo(() => {
        const startIdx = (safePage - 1) * perPage;
        return sortedData.slice(startIdx, startIdx + perPage);
    }, [sortedData, safePage, perPage]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleSort = useCallback((colKey) => {
        setSortColumn((prev) => {
            if (prev === colKey) {
                setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortOrder('asc');
            return colKey;
        });
        setPage(1);
    }, []);

    const handleColumnFilterChange = useCallback((columnKey, selectedValues) => {
        setColumnFilters((prev) => ({ ...prev, [columnKey]: selectedValues }));
        setPage(1);
    }, []);

    const handleClearColumnFilters = useCallback(() => {
        setColumnFilters({});
        setPage(1);
    }, []);


    const handleSearchChange = useCallback((e) => {
        setSearch(e.target.value);
        setPage(1);
    }, []);

    const handleCampusChange = useCallback((e) => {
        const newId = parseInt(e.target.value, 10);
        setCampusId(newId);
        updateSessionCampus(newId);
        /* The filter lists hold the old campus's ids, which would still be sent (A21). */
        setReportFilters(clearCampusFilters);
        setReportData([]);
        setSummaryData([]);
        setHourlyStats({});
        setStatus('init');
    }, []);

    // -- Filter drawer --
    /* Commits the value but never re-runs — the drawer's other controls wait for Apply
       Settings, so this must too. The popover adds its own re-run below. */
    const handleTimeWindowChange = useCallback((next) => {
        setReportFilters((prev) => ({ ...prev, timeWindow: normalizeTimeWindow(next) }));
    }, []);

    /* Popover variant: flagged rather than run per-change, so picking a start and then an
       end costs one report run instead of two. Consumed when the popover closes. */
    const windowChangedInPopoverRef = useRef(false);
    const handlePopoverTimeWindowChange = useCallback((next) => {
        handleTimeWindowChange(next);
        windowChangedInPopoverRef.current = true;
    }, [handleTimeWindowChange]);

    const handleApplyFilters = useCallback((filters) => {
        /* timeWindow comes from current state, not the drawer: the drawer leaves it out of
           its Apply payload so a value edited from the header still wins. */
        setReportFilters((prev) => ({ ...filters, timeWindow: prev.timeWindow }));
        setFilterDrawerOpen(false);
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    const handleClearReportFilters = useCallback(() => {
        setReportFilters(emptyReportFilters());
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    // Count active report-level filters
    /* Active filters, not selected items — the same badge on every report (audit A22). */
    const activeReportFilterCount = useMemo(() => countActiveFilters(reportFilters), [reportFilters]);

    // -- Note dialog --
    const handleOpenNoteDialog = useCallback((event) => {
        setNoteTarget(event);
        setNoteDialogOpen(true);
    }, []);

    const handleSaveNote = useCallback(async (eventId, noteText) => {
        setSaving(true);
        const result = await addEventNote(eventId, campusId, noteText, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('Note added successfully.');
            setNoteDialogOpen(false);
            setNoteTarget(null);
            handleRunReport();
        } else {
            showToast(result[1] || 'Failed to add note.', 'error');
        }
    }, [campusId, showToast, handleRunReport]);

    // -- Reason dialog --
    const handleOpenReasonDialog = useCallback((event) => {
        setReasonTarget(event);
        setReasonDialogOpen(true);
    }, []);

    const handleSaveReason = useCallback(async (eventId, newReasonId, oldReasonId) => {
        setSaving(true);
        const result = await editEventReason(eventId, newReasonId, oldReasonId, campusId, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('Reason updated successfully.');
            setReasonDialogOpen(false);
            setReasonTarget(null);
            handleRunReport();
        } else {
            showToast(result[1] || 'Failed to update reason.', 'error');
        }
    }, [campusId, showToast, handleRunReport]);

    // -- Export (detailed) --
    const handleExportCsv = useCallback(() => {
        let maxNotes = 0;
        sortedData.forEach((row) => {
            if (row.notes && row.notes.length > maxNotes) {
                maxNotes = row.notes.length;
            }
        });
        exportAlertDetailsCsv(sortedData, maxNotes, 'hourly-details.csv', visibleColumns);
    }, [sortedData, visibleColumns]);

    const handleExportPdf = useCallback(() => {
        exportAlertDetailsPdf(sortedData, 'Hourly Summary Report — Detailed', `${startDisplay} - ${endDisplay}`, 'hourly-details.pdf', visibleColumns);
    }, [sortedData, startDisplay, endDisplay, visibleColumns]);

    // -- Export (summary) --
    const handleExportSummaryCsv = useCallback(() => {
        exportHourlySummaryCsv(summaryData, 'hourly-summary.csv', isGrandstream);
    }, [summaryData, isGrandstream]);

    const handleExportSummaryPdf = useCallback(() => {
        exportHourlySummaryPdf(summaryData, 'Hourly Summary Report', `${startDisplay} - ${endDisplay}`, 'hourly-summary.pdf', isGrandstream);
    }, [summaryData, startDisplay, endDisplay, isGrandstream]);

    // Schedule
    const handleScheduleSave = useCallback(async (payload) => {
        const result = await createSchedule(payload, { campusId, userId: getUserId(), clientId: getSessionId() });
        if (result[0]) { showToast('Schedule created successfully.'); setScheduleDrawerOpen(false); }
        else { showToast(result[1] || 'Failed to create schedule.', 'error'); }
    }, [campusId, showToast]);

    // ======================================================================
    // Render helpers
    // ======================================================================
    /* utils/reportFilterChips.js — names resolved per sub-section idKey (audit B16). */
    const reportFilterChips = useMemo(() => buildFilterChips(reportFilters, filterOptions), [reportFilters, filterOptions]);

    // ======================================================================
    // Render
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ps-page">

                    {/* Toast */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* PAGE HEADER */}
                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            {/* Breadcrumb + Campus Selector */}
                            <div className="ps-breadcrumb-row">
                                <div className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ps-breadcrumb__current">Hourly Summary</span>
                                </div>

                                {/* Campus selector */}
                                <div className="ps-campus-row">
                                    <label htmlFor="hs-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="hs-campus-selector"
                                            value={campusId}
                                            onChange={handleCampusChange}
                                            className="ps-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            {/* Page title + time summary + actions row */}
                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ps-page-title">Hourly Summary</h1>
                                </div>
                                <div className="ar-title-row__actions">
                                    <div className="ar-time-badge-wrap" ref={timePopoverRef}>
                                        <span
                                            className={`ar-time-summary${timePopoverOpen ? ' ar-time-summary--open' : ''}`}
                                            onClick={() => setTimePopoverOpen((p) => !p)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTimePopoverOpen((p) => !p); } }}
                                            aria-expanded={timePopoverOpen}
                                            aria-haspopup="true"
                                        >
                                            <i className="fe fe-clock" style={{ marginRight: '4px', fontSize: 'var(--font-size-xs)' }} />
                                            {customRange
                                                ? `${formatDateTime12Hour(start)} – ${formatDateTime12Hour(end)}`
                                                : rangeName}
                                            {describeTimeWindow(reportFilters.timeWindow?.start, reportFilters.timeWindow?.end)
                                                ? `, ${describeTimeWindow(reportFilters.timeWindow?.start, reportFilters.timeWindow?.end)}`
                                                : ''}
                                            <svg className="ar-time-summary__chevron" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                            </svg>
                                        </span>

                                        {timePopoverOpen ? (
                                            <div className="ar-time-popover">
                                                <div className="ar-time-popover__grid">
                                                    {TIME_RANGES.map((range) => (
                                                        <button
                                                            key={range.id}
                                                            type="button"
                                                            className={`ar-time-popover__btn${rangeId === range.id && !customRange ? ' ar-time-popover__btn--active' : ''}`}
                                                            onClick={() => handleQuickTimeRange(range)}
                                                        >
                                                            {range.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="ar-time-popover__divider" />
                                                <button
                                                    type="button"
                                                    className={`ar-time-popover__custom${customRange ? ' ar-time-popover__custom--active' : ''}`}
                                                    onClick={handlePopoverCustom}
                                                >
                                                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                                    </svg>
                                                    Custom Range…
                                                </button>
                                                <div className="ar-time-popover__divider" />
                                                <div style={{ padding: 'var(--space-2)' }}>
                                                    <span
                                                        className="ar-filter-section__label"
                                                        style={{ display: 'block', marginBottom: 'var(--space-1)' }}
                                                    >
                                                        Time of Day
                                                    </span>
                                                    <TimeWindowPicker
                                                        compact
                                                        value={reportFilters.timeWindow}
                                                        onChange={handlePopoverTimeWindowChange}
                                                    />
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                    <span className="ar-disabled-tooltip-wrap">
                                        <button
                                            className="dp-btn dp-btn--secondary dp-btn--sm"
                                            onClick={() => setScheduleDrawerOpen(true)}
                                            disabled={customRange}
                                        >
                                            <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                        </button>
                                        {customRange ? (
                                            <span className="ar-disabled-tooltip" role="tooltip">
                                                Static custom dates are not eligible for recurring scheduled reporting.
                                            </span>
                                        ) : null}
                                    </span>
                                    <button
                                        className="dp-btn dp-btn--secondary dp-btn--sm"
                                        onClick={() => setFilterDrawerOpen(true)}
                                        style={{ position: 'relative' }}
                                    >
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeReportFilterCount > 0 ? (
                                            <span className="ar-filter-badge">{activeReportFilterCount}</span>
                                        ) : null}
                                    </button>
                                    <button
                                        className="dp-btn dp-btn--primary dp-btn--sm"
                                        onClick={() => handleRunReport({ force: true })}
                                        disabled={status === 'loading'}
                                    >
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* View toggle — pinned between header and scrollable content */}
                    {status === 'ready' && !overLimit ? (
                        <div className="ar-subtab-bar">
                            <div className="pp-subtab-toggle">
                                <button
                                    className={`pp-subtab-btn${viewMode === 'summary' ? ' is-active' : ''}`}
                                    onClick={() => setViewMode('summary')}
                                >
                                    Summary
                                </button>
                                <button
                                    className={`pp-subtab-btn${viewMode === 'detailed' ? ' is-active' : ''}`}
                                    onClick={() => setViewMode('detailed')}
                                >
                                    Detailed
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {/* MAIN CONTENT */}
                    <div className="ar-content">

                    {/* Report filter chips */}
                    {reportFilterChips.length > 0 ? (
                        <div className="ar-filter-chips">
                            {reportFilterChips.map((chip) => (
                                <span key={chip.key} className="ar-filter-chip">{chip.label}</span>
                            ))}
                            <button
                                className="ar-filter-chips__clear"
                                onClick={handleClearReportFilters}
                            >
                                Clear All
                            </button>
                        </div>
                    ) : null}

                    {/* Content area */}
                    <ReportRestoreOffer
                        offer={restore.offer}
                        onAccept={handleAcceptRestore}
                        onDismiss={restore.dismissOffer}
                        show={status === 'init'}
                    />
                    <ReportRestoreNote
                        servedFrom={restore.servedFrom}
                        refreshing={restore.refreshing}
                    />

                    {status === 'init' ? (
                        <div className="ar-init-message">
                            <i className="fe fe-bar-chart-2" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                            <p>Select a time range and click <strong>Run Report</strong> to generate report.</p>
                            <button
                                className="dp-btn dp-btn--primary dp-btn--sm"
                                onClick={() => handleRunReport({ force: true })}
                                disabled={status === 'loading'}
                                style={{ marginTop: 'var(--space-3)' }}
                            >
                                <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                {status === 'loading' ? 'Running...' : 'Run Report'}
                            </button>
                        </div>
                    ) : null}

                    {status === 'loading' ? (
                        <div className="ar-loading">
                            <div className="ar-loading__spinner" />
                            <p>Loading report data...</p>
                        </div>
                    ) : null}

                    {status === 'ready' && overLimit ? (
                        <div className="ar-overlimit">
                            <i className="fe fe-alert-triangle" style={{ fontSize: '32px', color: 'var(--color-warning)' }} />
                            <h3>Report Too Large</h3>
                            <p>This report contains more than 5,000 events. Summary view is available below.</p>
                            {csvUrl ? (
                                <a href={csvUrl} className="dp-btn dp-btn--primary" target="_blank" rel="noopener noreferrer">
                                    <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                    Download Full Report (CSV)
                                </a>
                            ) : null}
                        </div>
                    ) : null}

                    {status === 'ready' ? (
                        <>
                            {/* Summary view */}
                            {viewMode === 'summary' || overLimit ? (
                                <>
                                    {/* Summary export controls */}
                                    {summaryData.length > 0 ? (
                                        <div className="hs-summary-controls">
                                            <button className="rp-btn rp-btn--secondary" onClick={handleExportSummaryCsv}>
                                                <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                                CSV
                                            </button>
                                            <button className="rp-btn rp-btn--secondary" onClick={handleExportSummaryPdf}>
                                                <i className="fe fe-file-text" style={{ marginRight: '4px' }} />
                                                PDF
                                            </button>
                                        </div>
                                    ) : null}
                                    <HourlySummaryView
                                        summaryData={summaryData}
                                        hourlyStats={hourlyStats}
                                        startDisplay={startDisplay}
                                        endDisplay={endDisplay}
                                        start={start}
                                        end={end}
                                        reportFilters={reportFilters}
                                        isGrandstream={isGrandstream}
                                        isCloud={Boolean(campusConfig?.is_cloud)}
                                    />
                                </>
                            ) : null}

                            {/* Detailed view */}
                            {viewMode === 'detailed' && !overLimit ? (
                                <div className="ar-detail-view">
                                    {/* Controls row */}
                                    <div className="ap-controls">
                                        <div className="ap-controls__left">
                                            <div className="ap-search">
                                                <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                                </svg>
                                                <input
                                                    type="search"
                                                    placeholder="Search events..."
                                                    value={search}
                                                    onChange={handleSearchChange}
                                                    className="ap-search__input"
                                                    aria-label="Search events"
                                                />
                                            </div>
                                        </div>
                                        <div className="ap-controls__right">
                                            <ColumnVisibilityToggle
                                                columns={availableColumnDefs}
                                                visibleColumns={visibleColumns}
                                                onToggle={handleToggleColumn}
                                            />
                                            {status === 'ready' && !overLimit ? (
                                                <>
                                                    <button className="rp-btn rp-btn--secondary" onClick={handleExportCsv}>
                                                        <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                                        CSV
                                                    </button>
                                                    <button className="rp-btn rp-btn--secondary" onClick={handleExportPdf}>
                                                        <i className="fe fe-file-text" style={{ marginRight: '4px' }} />
                                                        PDF
                                                    </button>
                                                </>
                                            ) : null}
                                        </div>
                                    </div>

                                    <AlertDetailsTable
                                        data={paginatedData}
                                        allData={reportData}
                                        visibleColumns={visibleColumns}
                                        sortColumn={sortColumn}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                        filters={columnFilters}
                                        onFilterChange={handleColumnFilterChange}
                                        onClearFilters={handleClearColumnFilters}
                                        onAddNote={handleOpenNoteDialog}
                                        onEditReason={handleOpenReasonDialog}
                                        eventNotesActive={eventNotesActive}
                                        isReasonEditable={isReasonEditable}
                                        tzOffset={tzOffset}
                                        dateFilterMode="hour"
                                        isGrandstream={isGrandstream}
                                    />

                                    {/* Pagination */}
                                    <Pagination
                                        page={safePage}
                                        totalPages={totalPages}
                                        total={totalFiltered}
                                        perPage={perPage}
                                        onPageChange={setPage}
                                        onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                                        entityLabel="events"
                                        selectId="hs-per-page"
                                    />
                                    </div>
                            ) : null}
                        </>
                    ) : null}

                    </div>{/* end ar-content */}

                    {/* Filter Drawer */}
                    <ReportFilterDrawer
                        showTimeWindow
                        timeWindow={reportFilters.timeWindow}
                        onTimeWindowChange={handleTimeWindowChange}
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        onClear={handleClearReportFilters}
                        filterOptions={filterOptions}
                        currentFilters={reportFilters}
                        saving={status === 'loading'}
                        timeRanges={TIME_RANGES}
                        rangeId={rangeId}
                        customRange={customRange}
                        start={start}
                        end={end}
                        onTimeRangeChange={handleTimeRange}
                        onCustomToggle={handleCustomToggle}
                        onStartChange={(date) => setStart(date)}
                        onEndChange={(date) => setEnd(date)}
                    />

                    {/* Schedule Report Drawer */}
                    <ScheduleReportDrawer
                        open={scheduleDrawerOpen}
                        onClose={() => setScheduleDrawerOpen(false)}
                        onSave={handleScheduleSave}
                        saving={false}
                        reportTypeId={REPORT_TYPE_ID.HOURLY_SUMMARY}
                        rangeId={rangeId}
                        rangeName={customRange ? 'Custom' : rangeName}
                        reportFilters={reportFilters}
                        filterOptions={filterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />

                    {/* Event Note Dialog */}
                    {noteDialogOpen && noteTarget ? (
                        <EventNoteDialog
                            event={noteTarget}
                            prenoteData={prenoteData}
                            eventNotesPreform={eventNotesPreform}
                            eventNotesFreeform={eventNotesFreeform}
                            tzOffset={tzOffset}
                            onClose={() => { setNoteDialogOpen(false); setNoteTarget(null); }}
                            onSave={handleSaveNote}
                            saving={saving}
                        />
                    ) : null}

                    {/* Edit Reason Dialog */}
                    {reasonDialogOpen && reasonTarget ? (
                        <EditReasonDialog
                            event={reasonTarget}
                            reasonCodes={filterOptions.reasonCodes || []}
                            onClose={() => { setReasonDialogOpen(false); setReasonTarget(null); }}
                            onSave={handleSaveReason}
                            saving={saving}
                        />
                    ) : null}

                </div>
            </div>
        </>
    );
};

export default HourlySummaryPage;
